import React from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import useCommsManager from '../hooks/useCommsManager'
import { TEXT, cardSx, pageSx } from '../theme/tokens'

/**
 * Renders `children` only for someone who may change what gets sent on this
 * host (hooks/useCommsManager: comms-tool-dev on dev, comms-tool-prod on
 * production); everyone else gets the reason and a way back. The list pages
 * hide or disable their change controls with the same rule; this covers a
 * typed or bookmarked link.
 *
 * @param {{title: string, action: string, elsewhere: string, backTo: string,
 *     backLabel: string, children: node}} props `action` names what needs
 *     the role ("Creating and editing pipelines"); `elsewhere` is shown on a
 *     host with no manager role (a local or staging build)
 */
const CommsManagerGate = ({
    children,
    title,
    action,
    elsewhere,
    backTo,
    backLabel,
}) => {
    const navigate = useNavigate()
    const { canManage, role } = useCommsManager()
    if (canManage) return children
    return (
        <Box sx={pageSx}>
            <Box sx={{ maxWidth: 640, mx: 'auto', p: 4 }}>
                <Box sx={{ ...cardSx, p: 3 }}>
                    <Typography
                        variant="h6"
                        sx={{ color: TEXT.primary, mb: 1 }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: TEXT.secondary, mb: 2 }}
                    >
                        {role ? `${action} needs the ${role} role.` : elsewhere}
                    </Typography>
                    <Button variant="outlined" onClick={() => navigate(backTo)}>
                        {backLabel}
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}

CommsManagerGate.propTypes = {
    children: PropTypes.node,
    title: PropTypes.string.isRequired,
    action: PropTypes.string.isRequired,
    elsewhere: PropTypes.string.isRequired,
    backTo: PropTypes.string.isRequired,
    backLabel: PropTypes.string.isRequired,
}

export default CommsManagerGate
